import { createSignal, For, Show } from "solid-js";
import { DumpIcon } from "../components/DumpIcon";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";
import { CrudCollectionPage, type CrudRecord } from "./CrudCollectionPage";

export function VehiclesPage() {
  return (
    <CrudCollectionPage
      label="Vehicles"
      singular="Vehicle"
      endpoint="/vehicles"
      icon="nav_profiles"
      canCreate={hasPermission("vehicles.create")}
      canUpdate={hasPermission("vehicles.update")}
      createDefaults={{
        vin: null,
        plate: "",
        model: "",
        imageUrl: null,
        description: "",
        citizenId: null,
        color: null,
        status: "valid",
      }}
      advancedFields={[
        { key: "plate", label: "Plate" },
        { key: "citizen", label: "Owner State ID", type: "number" },
        { key: "createdFrom", label: "Date From", type: "datetime-local" },
        { key: "createdTo", label: "Date To", type: "datetime-local" },
      ]}
      fields={[
        { key: "plate", label: "Plate" },
        { key: "vin", label: "VIN" },
        { key: "model", label: "Model" },
        { key: "imageUrl", label: "Image URL" },
        { key: "description", label: "Description" },
        { key: "citizenId", label: "Owner CID", type: "number" },
        { key: "ownerName", label: "Owner", readOnly: true },
        { key: "color", label: "Color" },
        { key: "status", label: "Status" },
      ]}
      itemTitle={(item) => value(item.plate) || "No plate"}
      itemDescription={(item) =>
        [value(item.model), value(item.ownerName)].filter(Boolean).join(" • ")
      }
      itemAvatar={(item) => value(item.imageUrl) || null}
      prepareWrite={(item) => ({
        vin: nullableText(item.vin),
        plate: value(item.plate),
        model: value(item.model),
        imageUrl: nullableText(item.imageUrl),
        description: value(item.description),
        citizenId: nullableNumber(item.citizenId),
        color: nullableText(item.color),
        status: value(item.status) || "valid",
      })}
      renderAfterFields={(item, reload) => (
        <VehiclePoints item={item} reload={reload} />
      )}
    />
  );
}

function VehiclePoints(props: { item: CrudRecord; reload: () => Promise<void> }) {
  const [description, setDescription] = createSignal("");
  const [points, setPoints] = createSignal(1);
  const [busy, setBusy] = createSignal(false);
  const records = () =>
    Array.isArray(props.item.points) ? (props.item.points as CrudRecord[]) : [];

  const add = async () => {
    if (!props.item.id) return;
    setBusy(true);
    try {
      await apiRequest(`/vehicles/${props.item.id}/points`, {
        method: "POST",
        token: currentUser()?.accessToken,
        body: {
          points: points(),
          description: description(),
          expiresAt: null,
        },
      });
      setDescription("");
      await props.reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: unknown) => {
    if (!props.item.id || !id) return;
    setBusy(true);
    try {
      await apiRequest(`/vehicles/${props.item.id}/points/${String(id)}`, {
        method: "DELETE",
        token: currentUser()?.accessToken,
      });
      await props.reload();
    } finally {
      setBusy(false);
    }
  };

  const edit = async (point: CrudRecord) => {
    if (!props.item.id || !point.id) return;
    const descriptionValue = window.prompt("Vehicle action flag description", value(point.description));
    if (descriptionValue === null) return;
    setBusy(true);
    try {
      await apiRequest(`/vehicles/${props.item.id}/points/${String(point.id)}`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: { description: descriptionValue },
      });
      await props.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="bg-[rgba(255,255,255,.04)] p-3">
      <div class="mb-3 flex items-center justify-between">
        <p class="roboto500">Vehicle Action Flag Points</p>
        <Show when={hasPermission("vehicles.vaf.create")}>
          <div class="flex gap-2">
            <input
              class="h-7 w-14 bg-white-11 px-2"
              type="number"
              min="1"
              value={points()}
              onInput={(event) =>
                setPoints(Number(event.currentTarget.value) || 1)
              }
            />
            <input
              class="h-7 w-64 bg-white-11 px-2"
              placeholder="Description"
              value={description()}
              onInput={(event) => setDescription(event.currentTarget.value)}
            />
            <button
              class="flex h-7 items-center bg-green-gradient px-2 disabled:opacity-40"
              disabled={busy() || !props.item.id}
              onClick={() => void add()}
            >
              <DumpIcon name="plus" />
              <span class="pl-2">Add VAF</span>
            </button>
          </div>
        </Show>
      </div>
      <Show
        when={records().length}
        fallback={<p class="text-sm text-white-35">No active VAF points.</p>}
      >
        <div class="flex flex-col gap-2">
          <For each={records()}>
            {(point) => (
              <div class="item-list-bg flex items-center justify-between bg-gray-gradient px-3 py-2">
                <div>
                  <p>{value(point.description)}</p>
                  <p class="text-xs text-white-55">
                    {value(point.points)} point(s) • ID: {value(point.id)}
                  </p>
                </div>
                <div class="flex gap-1">
                <Show when={hasPermission("vehicles.vaf.update")}>
                  <button class="bg-blue-gradient px-2 text-xs" disabled={busy()} onClick={() => void edit(point)}>Edit</button>
                </Show>
                <Show when={hasPermission("vehicles.vaf.revoke")}>
                  <button
                    class="flex h-7 w-7 items-center justify-center bg-red-gradient"
                    disabled={busy()}
                    onClick={() => void remove(point.id)}
                  >
                    <DumpIcon name="tab_close" />
                  </button>
                </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function value(input: unknown): string {
  return typeof input === "string" || typeof input === "number"
    ? String(input)
    : "";
}
function nullableText(input: unknown): string | null {
  const output = value(input).trim();
  return output ? output : null;
}
function nullableNumber(input: unknown): number | null {
  const output = Number(input);
  return Number.isSafeInteger(output) && output > 0 ? output : null;
}
